import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, ExternalLink, Copy, CircleCheck as CheckCircle, Building2, ShoppingCart } from 'lucide-react';
import { KioskService } from '../services/KioskService';

interface LeagueRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  primary_color: string;
  signup_mode: string;
  created_at: string;
}

export const AdminKioskLeagues: React.FC = () => {
  const [leagues, setLeagues] = useState<LeagueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<LeagueRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [duplicateLeague, setDuplicateLeague] = useState<LeagueRow | null>(null);
  const [duplicating, setDuplicating] = useState(false);

  const [form, setForm] = useState({ name: '', slug: '', description: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const data = await KioskService.adminListLeagues();
      setLeagues((data as LeagueRow[]).filter(l => l.signup_mode !== 'passcode'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const slug = (form.slug || form.name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      if (!slug) throw new Error('Slug is required');
      await KioskService.adminCreateLeague({
        name: form.name,
        slug,
        description: form.description,
        status: 'draft',
      });
      setShowCreate(false);
      setForm({ name: '', slug: '', description: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await KioskService.adminDeleteLeague(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const handleDuplicateLeague = async (name: string, slug: string) => {
    if (!duplicateLeague) return;
    try {
      setDuplicating(true);
      await KioskService.adminDuplicateLeague(duplicateLeague.id, { name, slug });
      setDuplicateLeague(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to duplicate');
    } finally {
      setDuplicating(false);
    }
  };

  const copyUrl = async (slug: string) => {
    const url = `${window.location.origin}/league/${slug}`;
    await navigator.clipboard.writeText(url);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 1500);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="w-7 h-7 text-sky-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Kiosk Leagues</h1>
            <p className="text-sm text-slate-400">White-label singing competitions for external clients</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/contests"
            target="_blank"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30 rounded-lg transition text-sm font-medium"
          >
            <ShoppingCart className="w-4 h-4" /> Storefront
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </Link>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition"
          >
            <Plus className="w-4 h-4" /> New League
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-center py-12">Loading...</div>
      ) : leagues.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl text-slate-400">
          No kiosk leagues yet. Create one to get started.
        </div>
      ) : (
        <div className="grid gap-4">
          {leagues.map((league) => (
            <div
              key={league.id}
              className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: league.primary_color || '#0ea5e9' }}
                >
                  {league.name.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-white font-semibold">{league.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="font-mono">/league/{league.slug}</span>
                    <StatusBadge status={league.status} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyUrl(league.slug)}
                  className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition"
                  title="Copy public URL"
                >
                  {copiedSlug === league.slug ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
                <a
                  href={`/league/${league.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition"
                  title="Open public page"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to={`/admin/kiosk-leagues/${league.id}`}
                  className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                >
                  Manage
                </Link>
                <button
                  onClick={() => setDuplicateLeague(league)}
                  className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition"
                  title="Duplicate league"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setConfirmDelete(league)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create Kiosk League">
          <form onSubmit={handleCreate} className="space-y-4">
            <Field label="League Name">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                required
              />
            </Field>
            <Field label="URL Slug" hint="Leave blank to auto-generate from name. Letters, numbers, hyphens only.">
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono"
                placeholder="acme-radio-summer-sing-off"
              />
            </Field>
            <Field label="Description">
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white h-24"
              />
            </Field>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-slate-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg"
              >
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} title="Delete League">
          <p className="text-slate-300 mb-4">
            Delete "{confirmDelete.name}"? This will remove all participants, submissions, and signups. This cannot be undone.
          </p>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-slate-300">Cancel</button>
            <button onClick={handleDelete} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg">
              Delete
            </button>
          </div>
        </Modal>
      )}

      {duplicateLeague && (
        <DuplicateLeagueModal
          league={duplicateLeague}
          loading={duplicating}
          onClose={() => setDuplicateLeague(null)}
          onConfirm={handleDuplicateLeague}
        />
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, string> = {
    draft: 'bg-slate-600 text-slate-200',
    open: 'bg-green-500/20 text-green-300 border border-green-500/30',
    closed: 'bg-amber-500/20 text-amber-300 border border-amber-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};

const Modal: React.FC<{ onClose: () => void; title: string; children: React.ReactNode }> = ({ onClose, title, children }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white">&times;</button>
      </div>
      {children}
    </div>
  </div>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

interface DuplicateLeagueModalProps {
  league: LeagueRow;
  loading: boolean;
  onClose: () => void;
  onConfirm: (name: string, slug: string) => void;
}

const DuplicateLeagueModal: React.FC<DuplicateLeagueModalProps> = ({ league, loading, onClose, onConfirm }) => {
  const [name, setName] = useState(`Copy of ${league.name}`);
  const [slug, setSlug] = useState(`copy-of-${league.slug}`);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-2">Duplicate League</h3>
        <p className="text-sm text-white/60 mb-4">
          This will create a new league with the same settings, branding, and songs. Participants and submissions will not be copied.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">League Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-sky-400 focus:outline-none"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-sky-400 focus:outline-none font-mono text-sm"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-slate-600 text-white rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(name, slug)}
            disabled={loading || !name.trim() || !slug.trim()}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-sky-500/25 transition disabled:opacity-50"
          >
            {loading ? 'Duplicating...' : 'Duplicate'}
          </button>
        </div>
      </div>
    </div>
  );
};
